<template>
  <div style="position: relative">
    <div class="mb-2" v-if="!form">
      <v-btn
        v-if="editable && creatable"
        @click="add"
        class="pink white--text"
      >
        <v-icon left dark>add</v-icon>
        {{ $t('crud.add') }}
      </v-btn>

      <v-btn
        v-if="serverside && refreshable"
        @click="refresh"
        style="float: right"
        class="blue darken-1 white--text button-refresh"
      >
        <v-icon dark>refresh</v-icon>
      </v-btn>

      <v-btn
        v-if="exportable"
        @click="exportCsv"
        class="blue darken-1 white--text"
        style="float: right"
      >
        <v-icon left dark>save</v-icon>
        {{ $t('crud.csv') }}
      </v-btn>

      <v-btn
        v-if="filtering"
        @click.stop="showFilters"
        :class="{ blue: true, 'darken-1': true, 'white--text': true, 'button-active-filter': isAnyFilterActive() }"
        style="float: right"
      >
        <v-icon left dark>filter</v-icon>
        {{ $t('crud.filters') }}
      </v-btn>
    </div>

    <div v-if="!form" class="table__overflow elevation-1">
      <table class="datatable table table-crud">
        <thead>
          <tr>
            <th v-if="isBulkEnabled" class="check column"><input ref="checkAll" type="checkbox" @change="checkAll" /></th>
            <th
              v-for="field in fieldKeys"
              v-if="nFields[field].list"
              :key="field"
              @click="sort(field)"
              :class="{
                sortable: nFields[field].sortable,
                column: true,
                'text-xs-center': nFields[field].type === 'bool',
                'text-xs-left': nFields[field].type !== 'bool',
              }"
            >
              <v-icon v-if="isActiveFilter(field)" class="filter mr-1" v-tooltip="{ content: filterTooltip(field) }" @click.stop="showFilters">filter_none</v-icon>
              {{ nFields[field].label }}
              <v-icon :class="{ sorted: sortBy === field }">{{ sortAsc && sortBy === field ? 'arrow_downward' : 'arrow_upward' }}</v-icon>
            </th>
            <th class="actions" v-if="itemActions.length > 0 || editable || deletable"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-show="loading">
            <td colspan="100"><loadable :loading="true" :description="$t('loadingItems')" /></td>
          </tr>
          <tr class="no-items" v-show="!loading && items && items.length == 0">
            <td colspan="100">{{ $t('noItems') }}</td>
          </tr>
          <template v-if="!loading">
            <tr v-for="(item, index) in paginated" :key="item.id" class="column">
              <td v-if="isBulkEnabled" class="check"><input type="checkbox" v-model="checked[item._self]" /></td>
              <template v-for="field in fieldKeys" v-if="nFields[field].list">
                <slot :name="field" :item="item" :field="field" :value="item[field]">
                  <crud-field
                    :key="`${item.id}-${field}`"
                    ref="crudField"
                    v-model="paginated[index]"
                    :field="field"
                    :options="nFields[field]"
                    :editable="editable"
                    @save="save"
                    @edited="edited"
                  />
                </slot>
              </template>
              <td class="text-xs-right actions" v-if="itemActions.length > 0 || editable || deletable">
                <div class="actions">
                  <v-btn
                    :icon="action.icon && !action.label"
                    flat
                    v-for="(action, index) in itemActions"
                    v-if="action.visibility(item)"
                    :key="index"
                    :variant="item.variant"
                    @click.stop="action.click(item)"
                    class="ma-0"
                  >
                    <v-icon v-if="action.icon">{{ action.icon }}</v-icon>
                    <span v-if="action.label">{{ action.label }}</span>
                  </v-btn>
                  <v-btn class="ma-0" icon :title="$t('crud.edit')" v-if="editable" @click="edit(item)"><v-icon>edit</v-icon></v-btn>
                  <v-btn class="ma-0" icon :title="$t('crud.delete')" v-if="deletable" @click="remove(item)"><v-icon>delete</v-icon></v-btn>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="!form && (bulk || paginating)">
      <v-layout row wrap>
        <v-flex xs6>
          <div>
            <v-btn
              v-for="(action, index) in (bulk && selectedItems.length > 0 ? bulkActions : [])"
              :key="index"
              :error="action.color === 'error'"
              @click="actionOnSelected(action)"
            >
              <v-icon v-if="action.icon" left :dark="action.color === 'error'">{{ action.icon }}</v-icon>
              <span>{{ action.multiLabel || action.label }}</span>
            </v-btn>

            <v-btn v-if="deletable && bulk && selectedItems.length > 0 " error size="sm" @click="deleteSelected" >
              <v-icon left dark>delete</v-icon>
              <span>{{ $t('crud.deleteSelected') }}</span>
            </v-btn>

            <v-btn
              v-for="(action, index) in allItemsActions"
              :key="`allItems_${index}`"
              :error="action.color === 'error'"
              @click="allItemsActionSelected(action)"
            >
              <v-icon v-if="action.icon" left :dark="action.color === 'error'">{{ action.icon }}</v-icon>
              <span>{{ action.multiLabel || action.label }}</span>
            </v-btn>
          </div>
        </v-flex>

        <v-flex xs6 class="text-xs-right">
          <div v-if="!serverside || total">
            <v-pagination v-if="paginating" :length="Math.ceil(totalItems / perPage)" v-model="page" total-visible="3" class="mt-2"></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </div>

    <crud-filter-dialog v-model="filters" :fields="nFields" ref="filterDialog" @saved="saveFilters()" @cancel="clearFilters()" />

    <component
      v-if="form && formComponent" :is="formComponent"
      :v-model="form"
      :fields="fields"
      :form-order="formOrder"
      @save="formSave"
      @cancel="formCancel"
    />
    <crud-form
      ref="form"
      v-if="form && !formComponent"
      v-model="form"
      :fields="nFields"
      :form-order="formOrder"
      :error="formError"
      @save="formSave"
      @cancel="formCancel"
    />
  </div>
</template>

<script>
import CrudField from './CrudField'
import CrudForm from './CrudForm'
import CrudFilter from './CrudFilter'
import CrudFilterDialog from './CrudFilterDialog'
import DataChange from '@/models/DataChange'

import Csv from '@/utils/Csv'
import moment from 'moment'

export default {
  props: {
    fields: Object,
    items: {
      type: Array,
      default: () => []
    },
    actions: {
      type: Array,
      default: () => []
    },
    creatable: {
      type: Boolean,
      default: true
    },
    editable: {
      type: Boolean,
      default: true
    },
    deletable: {
      type: Boolean,
      default: true
    },
    exportable: {
      type: Boolean,
      default: true
    },
    refreshable: {
      type: Boolean,
      default: true
    },
    perPage: {
      type: Number,
      default: 25
    },
    bulk: {
      type: Boolean,
      default: true
    },
    filtering: {
      type: Boolean,
      default: true
    },
    paginating: {
      type: Boolean,
      default: true
    },
    formComponent: {
      type: Object,
      default: null
    },
    formOrder: {
      type: Array,
      default: null
    },
    defaultOrder: {
      type: String,
      default: null
    },
    defaultOrderAsc: {
      type: Boolean,
      default: true
    },
    serverside: {
      type: Boolean,
      default: false
    },
    total: {
      type: Number,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    requestAllItems: {
      type: Function,
      default: null
    },
    allItemsActions: {
      type: Array,
      default: () => []
    },
    formError: String
  },
  created () {
    this.clearFilters()
  },
  data () {
    return {
      form: false,
      page: 1,
      filters: {},
      checked: {},
      sortBy: this.defaultOrder,
      sortAsc: this.defaultOrderAsc
    }
  },
  computed: {
    fieldKeys () {
      return Object.keys(this.fields)
    },
    nActions () {
      return this.actions.map(action => {
        let item = {}
        for (let i in action) {
          item[i] = action[i]
        }
        if (item.single === undefined) {
          item.single = true
        }
        if (item.visibility === undefined) {
          item.visibility = () => true
        }
        return item
      })
    },
    itemActions () {
      return this.nActions.filter(item => item.single)
    },
    bulkActions () {
      return this.nActions.filter(item => item.multi)
    },
    nFields () {
      return this.fieldKeys.reduce((fields, field) => {
        let options = this.fields[field]
        let normalized = options

        /*
        for (let i in options) {
          normalized[i] = options[i]
        }
        */

        if (normalized.sortable === undefined) {
          normalized.sortable = true
        }
        if (normalized.filterable === undefined) {
          normalized.filterable = true
        }
        if (normalized.id === undefined) {
          normalized.id = '_self'
        }
        if (normalized.display === undefined) {
          normalized.display = 'name'
        }
        if (normalized.list === undefined) {
          normalized.list = true
        }
        if (normalized.editable === undefined) {
          normalized.editable = true
        }
        if (normalized.formOrder === undefined) {
          normalized.formOrder = 0
        }
        if (normalized.errors === undefined) {
          normalized.errors = []
        }
        if (normalized.rules === undefined) {
          normalized.rules = []
        }
        if (normalized.type === 'reference' ||
          normalized.type === 'references'
        ) {
          if (normalized.display === undefined) {
            normalized.display = 'name'
          }
        }

        fields[field] = normalized
        return fields
      }, {})
    },
    paginated () {
      let filtered = this.items

      if (!this.serverside) {
        filtered = filtered.filter(item => {
          let fieldFilter = this.fieldKeys.find(field => {
            let options = this.nFields[field]
            let idField = options.id || '_self'
            if (options.filterable) {
              if (this.filters[field]) {
                if (options.type === 'references') {
                  if (this.filters[field].length > 0 && !this.filters[field].find(fil => item[field].find(sub => sub[idField] === fil[idField]))) {
                    return true
                  }
                } else if (options.type === 'reference') {
                  if (this.filters[field].length > 0 && !this.filters[field].find(fil => fil[idField] === item[field][idField])) {
                    return true
                  }
                } else if (options.type === 'enum') {
                  if (this.filters[field].length > 0 && !this.filters[field].find(fil => fil._self === item[field])) {
                    return true
                  }
                } else if (options.type === 'bool') {
                  return item[field] !== this.filters[field]
                } else {
                  if (!item[field] || item[field].toString().toLowerCase().indexOf(this.filters[field].toString().toLowerCase()) < 0) {
                    return true
                  }
                }
              }
            }
            return false
          })

          if (fieldFilter) {
            return false
          }

          return true
        })
      }

      if (!this.serverside && this.sortBy) {
        filtered.sort((a, b) => {
          return (this.sortAsc ? 1 : -1) * this.compare(a, b)
        })
      }

      if (!this.serverside) {
        filtered = filtered
          .slice((this.page - 1) * this.perPage, this.page * this.perPage)
      }

      return filtered
    },
    selectedItems () {
      return this.paginated.reduce((selected, item) => {
        if (this.checked[item._self]) {
          selected.push(item)
        }
        return selected
      }, [])
    },
    totalItems () {
      return this.total === null ? this.items.length : this.total
    },
    isBulkEnabled () {
      return this.bulk && (this.deletable || this.bulkActions.length > 0)
    }
  },
  watch: {
    page () {
      if (this.bulk && this.$refs.checkAll) {
        this.$refs.checkAll.checked = false
      }
      this.$emit('page-changed', this.page)
    },
    sortBy () {
      this.$emit('sort-changed', this.sortBy, this.sortAsc)
    },
    sortAsc () {
      this.$emit('sort-changed', this.sortBy, this.sortAsc)
    }
  },
  methods: {
    changePage (page) {
      this.page = page
    },
    clearFilters () {
      this.filters = this.fieldKeys.reduce((filters, field) => {
        if (this.nFields[field].type === 'references' ||
        this.nFields[field].type === 'reference' ||
        this.nFields[field].type === 'enum'
      ) {
          filters[field] = []
        } else {
          filters[field] = null
        }
        return filters
      }, {})

      this.$emit('filters-changed', this.filters)
    },
    compare (a, b) {
      let options = this.nFields[this.sortBy]
      let displayField = options.display || 'id'
      let av = a[this.sortBy]
      let bv = b[this.sortBy]

      if (this.isNothing(av) && this.isNothing(bv)) return 0
      if (this.isNothing(av)) return -1
      if (this.isNothing(bv)) return 1

      if (options.type === 'references') {
        if (this.isEmpty(av) && this.isEmpty(bv)) return 0
        if (this.isEmpty(av)) return -1
        if (this.isEmpty(bv)) return 1
        av = av[0]
        bv = bv[0]
      }

      if (options.type === 'reference' || options.type === 'references') {
        if (this.isNothing(av[displayField]) && this.isNothing(bv[displayField])) return 0
        if (this.isNothing(av[displayField])) return -1
        if (this.isNothing(bv[displayField])) return 1
        return av[displayField].localeCompare(bv[displayField])
      } else if (options.type === 'number') {
        return av - bv
      } else {
        return av.toString().localeCompare(bv.toString())
      }
    },
    sort (field) {
      if (!this.nFields[field].sortable) return

      if (this.sortBy === field) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortBy = field
        this.sortAsc = true
      }
    },
    isNothing (ref) {
      return ref === null || ref === undefined
    },
    isEmpty (refs) {
      return this.isNothing(refs) || refs.length === 0
    },
    create (item) {
      this.$emit('create', item)
    },
    save (item, field) {
      this.$emit('save', item, [field], this.items)
    },
    remove (item) {
      this.$emit('remove', [item])
    },
    edit (item) {
      // References type currently requires array, so make sure we have array
      this.fieldKeys.forEach(field => {
        if (this.nFields[field].type === 'references' &&
          !item[field]
        ) {
          item[field] = []
        }
      })

      this.form = item
    },
    formSave (item) {
      this.$emit('save', item, this.fieldKeys, this.items)
    },
    formCancel (previous) {
      this.items.forEach((item, index) => {
        if (item._self === previous._self) {
          this.items[index] = previous
        }
      })

      this.form = null
      this.$emit('cancel', previous)
    },
    saveFailed (item, fields) {
      if (this.$refs.form) {
        this.$refs.form.saveFailed(item, fields)
      }
    },
    saved (item, fields) {
      if (fields.length === 1) {
        this.$refs.crudField.forEach(field => {
          field.saved(item, fields[0])
        })
      }
      this.form = null
    },
    edited (comp, item, field) {
      this.$refs.crudField.forEach(item => {
        if (item !== comp) item.closeForm()
      })
    },
    checkAll (e) {
      this.checked = this.paginated.reduce((checked, item) => {
        checked[item._self] = e.srcElement.checked
        return checked
      }, {})
    },
    deleteSelected () {
      if (this.selectedItems.length > 0) {
        this.$emit('remove', this.selectedItems)
      }
    },
    actionOnSelected (action) {
      if (this.selectedItems.length > 0) {
        action.action(this.selectedItems)
      }
    },
    allItemsActionSelected (action) {
      let promise = (this.serverside && this.requestAllItems)
        ? this.requestAllItems(this.filters)
        : Promise.resolve(this.items)

      promise.then(items => {
        action.action(items)
      })
    },
    exportCsv () {
      let promise = (this.serverside && this.requestAllItems)
        ? this.requestAllItems(this.filters)
        : Promise.resolve(this.items)

      promise.then(items => {
        let csv = new Csv(';', 'cp1250')

        csv.addRow(
          Object.keys(this.nFields)
            .filter(field => this.nFields[field].list)
            .map(field => this.nFields[field].label)
        )

        items.forEach(item => {
          csv.addRow(
            Object.keys(this.nFields)
              .filter(field => this.nFields[field].list)
              .map(field => {
                let options = this.nFields[field]
                let value = item[field]

                if (value === undefined ||
                  value === null
                ) {
                  return ''
                }

                switch (options.type) {
                  case 'date': return this.$format.date(value)
                  case 'reference': return value[options.display]
                  case 'references': return value.map(item => item[options.display]).join(', ')
                  case 'enum': return options.values[value] || value
                  case 'bool': return value ? this.$t('yes') : this.$t('no')
                  case 'data': return DataChange.getDiff(item, field).join(', ')
                  default: return value
                }
              })
          )
        })

        let exportName = this.$format.filename(this.$t(this.$route.meta.title))
        let exportDate = moment().format('L LT')

        csv.download(`${exportName} ${exportDate}.csv`)
      })
    },
    showFilters () {
      this.$refs.filterDialog.show()
    },
    saveFilters () {
      this.$emit('filters-changed', this.filters)
    },
    add () {
      this.edit(
        this.fieldKeys.reduce((item, field) => {
          let options = this.nFields[field]
          if (options.default) {
            item[field] = options.default
          } else if (options.type === 'references') {
            item[field] = []
          }
          return item
        }, {})
      )
    },
    isAnyFilterActive () {
      return !!this.fieldKeys.find(field => this.isActiveFilter(field))
    },
    isActiveFilter (field) {
      return this.filters[field] &&
        (['references', 'reference', 'enum'].indexOf(this.nFields[field].type) < 0 || this.filters[field].length > 0)
    },
    filterTooltip (field) {
      return this.$t('crud.filterTooltip', [ this.filterTooltipvalue(field) ])
    },
    filterTooltipvalue (field) {
      if (['references', 'reference', 'enum'].indexOf(this.nFields[field].type) < 0) {
        return this.filters[field]
      }

      return this.filters[field].map(item => {
        return item[this.nFields[field].display]
      }).join(', ')
    },
    refresh () {
      this.$emit('refresh')
    }
  },
  components: {
    CrudField,
    CrudForm,
    CrudFilter,
    CrudFilterDialog
  }
}
</script>

<style>
.table-crud {
  font-size: 90%;
}

.table-crud input[type=text],
  input[type=number]
{
  width: 80px !important;
}

.table-crud .check {
  width: 20px;
}

.table-crud td.actions {
  width: 30px;
}

.table-crud div.actions {
  white-space: nowrap;
  display: flex;
}

.ui.multiple.dropdown > .label {
  font-weight: normal;
}

i.icon.delete:before {
  content: "x";
  font-style: normal;
}

.no-items td {
  text-align: center;
  padding: 50px !important;
}

.datatable thead th.column.sortable i.sorted,
.datatable thead th.column.sortable i.filter {
  opacity: 0.85;
}

button.blue.darken-1.button-active-filter {
  box-shadow: none;
  font-weight: bold;
  background-color: #008000 !important;
}

.button-refresh {
  min-width: auto;
}

.button-refresh > .btn__content {
  padding: 0px 6px !important;
}
</style>



// WEBPACK FOOTER //
// Crud.vue?2ac7b358